import { createProfile } from "@/db/profiles";
import { createSnapshot } from "@/db/snapshots";
import type { NormalizedRow } from "@/types/schemas";
import { CatalogKitConfigSchema, DEFAULT_CONFIG, type CatalogKitConfig } from "@/types";

/**
 * One-time import of v0.1 state into v0.2. v0.1 kept everything in localStorage;
 * v0.2 introduces IndexedDB (profiles/snapshots). This is a data import, not a
 * Dexie schema upgrade (see `../migrations.ts`).
 *
 * The config schema is unchanged between v0.1 and v0.2 web builds, so migrating
 * config is a validate-and-carry-over; theme (`catalogkit.theme`) and one-time
 * flags are already read from localStorage by their own modules and need no
 * migration. The changelog modal is surfaced by the app shell based on
 * {@link needsMigration}; this module owns the data logic and the done-flag.
 */

/** v0.1 localStorage keys. */
export const LEGACY_CONFIG_KEY = "catalogkit.config";
/** Flag marking that the v0.1→v0.2 import has run. */
export const MIGRATED_FLAG = "catalogkit.flag.v02Migrated";

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable — migration simply cannot be marked done; safe to retry.
  }
}

/** Whether any v0.1 data is present in localStorage. */
export function hasLegacyData(): boolean {
  return safeGet(LEGACY_CONFIG_KEY) !== null;
}

/** Whether the migration flag has already been set. */
export function isMigrated(): boolean {
  return safeGet(MIGRATED_FLAG) === "1";
}

/**
 * True when we should run the v0.2 first-load flow (legacy data exists and the
 * migration has not been marked done). Drives the changelog modal.
 */
export function needsMigration(): boolean {
  return hasLegacyData() && !isMigrated();
}

/** Read and validate the v0.1 config, falling back to defaults on any problem. */
export function migrateConfig(): CatalogKitConfig {
  const raw = safeGet(LEGACY_CONFIG_KEY);
  if (raw === null) return DEFAULT_CONFIG;
  try {
    const parsed = CatalogKitConfigSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : DEFAULT_CONFIG;
  } catch {
    return DEFAULT_CONFIG;
  }
}

/** Mark the migration complete so the first-load flow does not run again. */
export function markMigrated(): void {
  safeSet(MIGRATED_FLAG, "1");
}

export interface MigrationOptions {
  /** Save the current in-memory session as the user's first profile. */
  saveSessionAsProfile?: boolean;
  /** Label for the created profile (default: "My Catalog"). */
  profileLabel?: string;
  /** Rows from the current in-memory session, captured as the first snapshot. */
  sessionRows?: NormalizedRow[];
  /** Original filename of the current session (for the snapshot). */
  sessionFilename?: string;
}

export interface MigrationResult {
  migratedConfig: CatalogKitConfig;
  createdProfileId: string | null;
  createdSnapshotId: string | null;
}

/**
 * Run the v0.1→v0.2 import.
 *
 * Migrates config, optionally saves the current session as the first profile
 * (with a first snapshot when rows are supplied), and marks the migration done.
 * Theme and other localStorage settings are preserved as-is.
 */
export async function migrate(options: MigrationOptions = {}): Promise<MigrationResult> {
  const migratedConfig = migrateConfig();
  let createdProfileId: string | null = null;
  let createdSnapshotId: string | null = null;

  if (options.saveSessionAsProfile) {
    const profile = await createProfile({
      label: options.profileLabel?.trim() || "My Catalog",
      source_system: "connectwise",
      default_config: migratedConfig,
    });
    createdProfileId = profile.id;

    if (options.sessionRows && options.sessionRows.length > 0) {
      const snapshot = await createSnapshot(profile.id, {
        source_filename: options.sessionFilename ?? "migrated-session.csv",
        rows: options.sessionRows,
        config_snapshot: migratedConfig,
        quality_score: 0,
        avg_margin_percent: 0,
        total_price_value: 0,
        label: "Imported from v0.1",
      });
      createdSnapshotId = snapshot.id;
    }
  }

  markMigrated();
  return { migratedConfig, createdProfileId, createdSnapshotId };
}
